import React, { useState } from 'react';
import { Form, FormGroup, Input, Button, Label, InputGroup, InputGroupText, Spinner } from 'reactstrap';
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate, Link } from "react-router-dom";
import axios from 'axios';
import { FiEye, FiEyeOff, FiMail, FiLock, FiChevronRight, FiLayers, FiCheck } from 'react-icons/fi';
import NoSesssionAuthPage from './../../Layouts/Shared/_NoSesssionAuthPage';

const BRAND_COLOR = "#013682";

export default function LoginPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [email, setEMail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordType, setPasswordType] = useState('password');
    const [isLoading, setIsLoading] = useState(false);

    // Tenant seçim ekranı state'leri
    const [showTenantSelection, setShowTenantSelection] = useState(false);
    const [tenantList, setTenantList] = useState([]);
    const [selectedTenantId, setSelectedTenantId] = useState(null);
    const [pendingPayload, setPendingPayload] = useState(null);
    const [isTenantLoading, setIsTenantLoading] = useState(false);

    const handleLogin = async (e) => {
        if (e) e.preventDefault();
        setIsLoading(true);

        const requestBody = { email, password };

        axios.defaults.headers.common["X-User-Language"] = t('language')?.toUpperCase();

        try {
            const ApiGateway = process.env.REACT_APP_API_URL;
            const response = await axios.post(ApiGateway + '/access/login/unauth', requestBody);
            const data = response.data;

            if (data?.isSucceed) {
                const payload = data?.payload;

                // Durum 2: session dolu → tenant zaten eşleşmiş, normal akış
                if (payload?.session !== null && payload?.session !== undefined) {
                    toast.success(t('api.default.service.response.succeded'), { autoClose: 500 });
                    sessionStorage.setItem("currentSession", JSON.stringify(payload));
                    setTimeout(() => {
                        navigate('/changed-language');
                    }, 800);
                    return;
                }

                // Durum 3: session null → tenant seçim ekranı
                const mpApplication = payload?.application?.id === "MP" ? payload.application : null;
                const tenants = mpApplication?.tenants ?? [];

                if (tenants.length > 0) {
                    setPendingPayload(payload);
                    setTenantList(tenants);
                    setShowTenantSelection(true);
                    setIsLoading(false);
                } else {
                    // Tenant listesi boşsa yine de devam et
                    sessionStorage.setItem("currentSession", JSON.stringify(payload));
                    toast.success(t('api.default.service.response.succeded'), { autoClose: 500 });
                    setTimeout(() => {
                        navigate('/changed-language');
                    }, 800);
                }
            } else {
                // Durum 1: Hatalı giriş
                data?.messages?.forEach(item =>
                    toast.error(item.message, { autoClose: 5000 })
                );
                setIsLoading(false);
            }
        } catch (error) {
            console.error(error);
            toast.error(t('api.default.service.response.error') || "Bir hata oluştu");
            setIsLoading(false);
        }
    };

    const handleTenantConfirm = async () => {
        if (!selectedTenantId) return;

        setIsTenantLoading(true);

        try {
            const ApiGateway = process.env.REACT_APP_API_URL;

            const response = await axios.put(
                `${ApiGateway}/access/login/change-domain?id=${encodeURIComponent(selectedTenantId)}`,
                null,
                {
                    headers: {
                        Authorization: `Bearer ${pendingPayload?.bearerToken}`,
                    },
                }
            );

            const data = response.data;

            if (data?.isSucceed) {
                toast.success(t('api.default.service.response.succeded'), { autoClose: 500 });
                sessionStorage.setItem("currentSession", JSON.stringify(data?.payload));
                setTimeout(() => {
                    navigate('/changed-language');
                }, 800);
            } else {
                data?.messages?.forEach(item =>
                    toast.error(item.message, { autoClose: 5000 })
                );
                setIsTenantLoading(false);
            }
        } catch (error) {
            console.error(error);
            toast.error(t('api.default.service.response.error') || "Bir hata oluştu");
            setIsTenantLoading(false);
        }
    };

    // Tenant Seçim Ekranı
    if (showTenantSelection) {
        return (
            <NoSesssionAuthPage title={t('pages.login.title')}>
                <ToastContainer />
                <div className="text-center text-lg-start mb-4">
                    <h2 className="fw-bold mb-2" style={{ color: BRAND_COLOR }}>
                        {t('pages.login.tenant.header') || 'Çalışma Alanı Seçin'}
                    </h2>
                    <p className="text-muted">
                        {t('pages.login.tenant.description') || 'Devam etmek için erişiminiz olan bir çalışma alanı seçin.'}
                    </p>
                </div>

                <div className="d-flex flex-column gap-2 mb-4" style={{ maxHeight: '340px', overflowY: 'auto' }}>
                    {tenantList.map((tenant) => {
                        const isSelected = selectedTenantId === tenant.id;
                        return (
                            <div
                                key={tenant.id}
                                onClick={() => setSelectedTenantId(tenant.id)}
                                style={{
                                    cursor: 'pointer',
                                    border: `2px solid ${isSelected ? BRAND_COLOR : '#dee2e6'}`,
                                    borderRadius: '10px',
                                    padding: '14px 16px',
                                    backgroundColor: isSelected ? `${BRAND_COLOR}08` : '#fff',
                                    transition: 'all 0.15s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <div className="d-flex align-items-center gap-3">
                                    <div
                                        style={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: '8px',
                                            backgroundColor: isSelected ? BRAND_COLOR : '#f0f4ff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.15s ease',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <FiLayers
                                            size={16}
                                            color={isSelected ? '#fff' : BRAND_COLOR}
                                        />
                                    </div>
                                    <div>
                                        <div className="fw-semibold" style={{ fontSize: '0.9rem', color: '#1a1a2e' }}>
                                            {tenant.name}
                                        </div>
                                        {tenant.groups?.length > 0 && (
                                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                {tenant.groups.map(g => g.name).join(', ')}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {isSelected && (
                                    <div
                                        style={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: '50%',
                                            backgroundColor: BRAND_COLOR,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <FiCheck size={13} color="#fff" strokeWidth={3} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <Button
                    block
                    size="lg"
                    disabled={!selectedTenantId || isTenantLoading}
                    style={{
                        backgroundColor: BRAND_COLOR,
                        borderColor: BRAND_COLOR,
                        opacity: !selectedTenantId ? 0.6 : 1,
                    }}
                    className="shadow"
                    onClick={handleTenantConfirm}
                >
                    {isTenantLoading ? (
                        <Spinner size="sm" />
                    ) : (
                        <>
                            {t('pages.login.tenant.button.confirm') || 'Devam Et'} <FiChevronRight className="ms-1" />
                        </>
                    )}
                </Button>

                <div className="text-center mt-3">
                    <button
                        type="button"
                        className="btn btn-link btn-sm text-muted text-decoration-none"
                        onClick={() => {
                            setShowTenantSelection(false);
                            setPendingPayload(null);
                            setSelectedTenantId(null);
                            setTenantList([]);
                        }}
                    >
                        ← {t('pages.login.tenant.button.back') || 'Geri Dön'}
                    </button>
                </div>
            </NoSesssionAuthPage>
        );
    }

    // Normal Login Ekranı
    return (
        <NoSesssionAuthPage title={t('pages.login.title')}>
            <ToastContainer />
            <div className="text-center text-lg-start mb-5">
                <h2 className="fw-bold mb-2" style={{ color: BRAND_COLOR }}>
                    {t('pages.login.header')}
                </h2>
                <p className="text-muted">{t('pages.login.description') || 'Sisteme devam etmek için giriş yapın'}</p>
            </div>

            <Form onSubmit={handleLogin}>
                {/* Email Alanı */}
                <FormGroup className="mb-4">
                    <Label className="small fw-bold text-uppercase text-secondary">
                        {t('pages.login.form.label.email')}
                    </Label>
                    <InputGroup className="corporate-input-group">
                        <InputGroupText className="bg-transparent border-end-0">
                            <FiMail size={18} className="text-muted" />
                        </InputGroupText>
                        <Input
                            type="email"
                            className="border-start-0 ps-0"
                            placeholder={t('pages.login.form.placeholder.email')}
                            onChange={e => setEMail(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleLogin()}
                            required
                        />
                    </InputGroup>
                </FormGroup>

                {/* Şifre Alanı */}
                <FormGroup className="mb-3">
                    <Label className="small fw-bold text-uppercase text-secondary">
                        {t('pages.login.form.label.password')}
                    </Label>
                    <InputGroup className="corporate-input-group">
                        <InputGroupText className="bg-transparent border-end-0">
                            <FiLock size={18} className="text-muted" />
                        </InputGroupText>
                        <Input
                            type={passwordType}
                            className="border-start-0 border-end-0 ps-0"
                            placeholder={t('pages.login.form.placeholder.password')}
                            onChange={e => setPassword(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleLogin()}
                            required
                        />
                        <InputGroupText
                            className="bg-transparent border-start-0 cursor-pointer"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setPasswordType(passwordType === 'password' ? 'text' : 'password')}
                        >
                            {passwordType === 'password' ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                        </InputGroupText>
                    </InputGroup>
                </FormGroup>

                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="form-check" style={{ visibility: 'hidden' }}>
                        <input type="checkbox" className="form-check-input" id="remember" />
                        <label className="form-check-label small" htmlFor="remember">
                            {t('pages.login.form.label.remember-me') || 'Beni Hatırla'}
                        </label>
                    </div>
                    <Link to="/reset-password-1" className="small text-decoration-none fw-bold" style={{ color: BRAND_COLOR }}>
                        {t('pages.login.form.label.reset-password-1')}
                    </Link>
                </div>

                {/* Submit Butonu */}
                <Button
                    block
                    size="lg"
                    disabled={isLoading}
                    style={{ backgroundColor: BRAND_COLOR, borderColor: BRAND_COLOR }}
                    className="shadow"
                    type="submit"
                >
                    {isLoading ? (
                        <Spinner size="sm" />
                    ) : (
                        <>
                            {t('pages.login.form.button.login')} <FiChevronRight className="ms-1" />
                        </>
                    )}
                </Button>
            </Form>
        </NoSesssionAuthPage>
    );
}
